import React, { useEffect, useState, useRef } from "react";
import "./summaryTable.css";
import "./filterStyles.css";
import BreadCrumbComp from "../../components/breadCrumb";
import { Grid } from "@mui/material";
import LaddaButton from "../../components/buttons/laddaButton";
import { useNavigate } from "react-router-dom";
import { icons } from "../../constants";
import SimpleTable from "../../components/tables/simpleTable";
import { useLoadingState } from "../../components/loadingFeedback";
import SimpleSelect from "../../components/selects/simpleSelect";
import { useForm } from "react-hook-form";
import { generateSelectList } from "../../utils/utilsFunctions";
import {
  resetOrderState,
  setEngineeringReportId,
  setServiceOrderId
} from "../../redux/slices/serviceOrderSlice";
import { useDispatch, useSelector } from "react-redux";
import {
  callApiAsync,
  callActionApiAsync,
  downloadEngineeringReportPDF,
  generateEngineeringReportPDF,
  getEngineeringActsTable,
  getSelectsServiceOrder
} from "../../services/apiService";
import { hasAnyRoleUtil, isRoleUtil } from "../../utils/AuthorizedUtils/360Report";
import { formatCustomDate } from "../../services/dateService";
import { engineerReportState } from "../../constants/enums";
import MultiSelect from "../../components/selects/multiSelect";
import { useEngineeringReportFilters } from "../../context/EngineeringReportFiltersContext";
import {
  BIOMEDICAL_ENGINEERING_REPORT_EDIT_ROLES,
  BIOMEDICAL_MODULE_ADMIN
} from "../../constants/biomedicalRoles";

const EMPTY_FILTERS = {
  serviceOrderId: "",
  serieNumber: "",
  institution: [],
  DateCompletion: "",
  engineer: ""
};

const normalizeFileNameSegment = (value) => {
  if (!value) return "";

  return String(value)
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-zA-Z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "")
    .replace(/_+/g, "_");
};

const buildEngineeringReportDownloadName = (rowData) => {
  const serviceOrderSegment =
    normalizeFileNameSegment(rowData?.service_order_name) || "REPORTE_INGENIERIA";

  const serials = Array.from(
    new Set(
      (rowData?.biomedical_equipment || [])
        .map((equipment) => equipment?.serie)
        .filter(Boolean)
    )
  )
    .map(normalizeFileNameSegment)
    .filter(Boolean);

  const serialSegment =
    serials.length === 0
      ? "SERIE_SIN_DATO"
      : serials.length <= 3
      ? `SERIE_${serials.join("_")}`
      : `SERIES_${serials.slice(0, 3).join("_")}_MAS_${serials.length - 3}`;

  return `RI_${serviceOrderSegment}_${serialSegment}.pdf`;
};

const ServiceRecords = () => {
  const [loadingTable, setLoadingTable] = useLoadingState(true);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [tableData, setTableData] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const [tableLoading, setTableLoading] = useState(false);
  const [tableError, setTableError] = useState("");
  const requestSequence = useRef(0);
  const pageSize = 15;
  const [listOptions, setListOptions] = useState({ orders: [], institutions: [], dates: [] });
  const [optionsReady, setOptionsReady] = useState(false);
  const [loadingBtnId, setLoadingBtnId] = useState(null);
  const pdfRequestInFlight = useRef(false);
  const [serieOId, setSerieOId] = useState([]);
  const [engineerList, setEngineerList] = useState([]);
  const isAndroid = /Android/i.test(navigator.userAgent);
  const { filters, setFilters, clearFilters } = useEngineeringReportFilters();
  const [appliedFilters, setAppliedFilters] = useState(filters || EMPTY_FILTERS);
  const userData = useSelector((state) => state.userData.userData);

  const urlApi = process.env.REACT_APP_API_URL;

  const triggerPdfDownload = (fullUrl, fileName) => {
    if (isAndroid) {
      window.open(fullUrl, "_blank");
      return;
    }

    const link = document.createElement("a");
    link.href = fullUrl;
    link.download = fileName;
    link.target = "_blank";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const getEngineeringReportPDF = async (rowData) => {
    const engineer_report_id = rowData?.id;
    if (!engineer_report_id || pdfRequestInFlight.current) return;

    pdfRequestInFlight.current = true;
    setLoadingBtnId(engineer_report_id);

    try {
      await callApiAsync(
        () => generateEngineeringReportPDF(engineer_report_id),
        (data) => {
          if (data?.path) {
            const cleanedPath = data.path.replace(/\\/g, "/");
            const fullUrl = `${urlApi}${cleanedPath}`;
            triggerPdfDownload(
              fullUrl,
              buildEngineeringReportDownloadName(rowData)
            );
          }
        }
      );
    } finally {
      pdfRequestInFlight.current = false;
      setLoadingBtnId(null);
    }
  };

  const downloadUpdatedEngineeringReportPDF = async (rowData) => {
    const engineer_report_id = rowData?.id;
    if (!engineer_report_id || pdfRequestInFlight.current) return;

    pdfRequestInFlight.current = true;
    setLoadingBtnId(engineer_report_id);

    try {
      await callApiAsync(
        () => downloadEngineeringReportPDF(engineer_report_id),
        (data) => {
          if (data?.path) {
            const fullUrl = `${urlApi}/${data.path}`;
            triggerPdfDownload(
              fullUrl,
              buildEngineeringReportDownloadName(rowData)
            );
          }
        }
      );
    } finally {
      pdfRequestInFlight.current = false;
      setLoadingBtnId(null);
    }
  };

  dispatch(resetOrderState());

  const {
    control: controlTable,
    getValues: getValuesTable,
    reset
  } = useForm({
    defaultValues: Object.keys(filters || {}).length ? filters : EMPTY_FILTERS
  });

  const engineeringReportColumns = [
    {
      field: "service_order_id",
      header: "service_order_id",
      columnType: "hide"
    },
    { field: "service_order_name", header: "OS", columnWidth: "140px" },
    { field: "activity_name", header: "Tipo de Orden de Servicio", columnWidth: "180px" },
    {
      field: "biomedical_equipment",
      header: "Nro de Serie",
      columnType: "columnList",
      property: "serie"
    },
    { field: "institutionId", header: "InstituciónId", columnType: "hide" },
    { field: "institution_name", header: "Institución", columnWidth: "230px" },
    {
      field: "engineer_name",
      header: "Ingeniero a cargo",
      columnWidth: "180px"
    },
    {
      field: "service_creation_date",
      header: "Fecha de realización",
      columnWidth: "150px",
      columnType: "custom",
      render: (rowData) => (
        <p className="table-default-column">
          {formatCustomDate(rowData.service_creation_date)}
        </p>
      )
    },
    { field: "actionButtons", columnType: "actionButtons" }
  ];

  const getTableData = async () => {
    const sequence = ++requestSequence.current;
    setTableLoading(true);
    setTableError("");
    const selectedDate = listOptions.dates.find((date) => date.value === appliedFilters.DateCompletion);
    await callActionApiAsync(
      () =>
        getEngineeringActsTable(
          userData.id,
          isModuleAdmin
            ? BIOMEDICAL_MODULE_ADMIN
            : canEditEngineeringReport
            ? "BIOMEDICAL_ENGINEERING_REPORT_ADMIN"
            : "BIOMEDICAL_INSPECTION_APPROVER",
          { page: currentPage, pageSize, filters: JSON.stringify({
            ...appliedFilters, date: selectedDate?.dateKey
          }) }
        ),
      (data) => {
        if (sequence !== requestSequence.current) return;
        setTableData(data.items);
        setTotalRecords(data.total);
        if (data.total > 0 && currentPage > Math.ceil(data.total / pageSize)) {
          setCurrentPage(Math.ceil(data.total / pageSize));
        }
      },
      () => {
        if (sequence !== requestSequence.current) return;
        setTableData([]);
        setTotalRecords(0);
        setTableError("No se pudieron cargar los reportes.");
      },
      setLoadingTable
    );
    if (sequence === requestSequence.current) setTableLoading(false);
  };
  const getFilterOptions = async () => {
    await callActionApiAsync(
      () => getEngineeringActsTable(userData.id,
        isModuleAdmin ? BIOMEDICAL_MODULE_ADMIN : canEditEngineeringReport
          ? "BIOMEDICAL_ENGINEERING_REPORT_ADMIN" : "BIOMEDICAL_INSPECTION_APPROVER",
        { view: "filters" }),
      (data) => {
        setListOptions(data);
        setSerieOId(data.series);
        setOptionsReady(true);
      },
      () => setTableError("No se pudieron cargar las opciones de filtro.")
    );
  };
  const getSelectData = async () => {
    callApiAsync(
      () => getSelectsServiceOrder(),
      (data) => {
        setEngineerList(data.enginnerList);
      }
    );
  };

  const buttons = [
    {
      type: "icon",
      icon: icons.descargar,
      isLoading: (rowData) => rowData.id === loadingBtnId,
      onClick: (rowData) => {
        getEngineeringReportPDF(rowData);
      },
      hideCondition: (row) =>
        row.engineering_act_state === engineerReportState.partialSave || 
        !!row.path_report 
    },
    {
      type: "icon",
      icon: icons.descargar,
      isLoading: (rowData) => rowData.id === loadingBtnId,
      onClick: (rowData) => {
        downloadUpdatedEngineeringReportPDF(rowData);
      },
      hideCondition: (row) =>
        row.engineering_act_state === engineerReportState.partialSave ||
        !row.path_report 
    },
    {
      type: "uploadBtn",
      icon: icons.upload,
      onClick: ({ fileName, originalName, path, rowData }) => {
        console.log("Archivo subido:", fileName);
      },
      onFinish: getTableData,
      hideCondition: (row) =>
        row.engineering_act_state === engineerReportState.partialSave ||
        !!row.received_by_sign || 
        !canEditEngineeringReport
    },
    {
      type: "icon",
      icon: icons.eye,
      onClick: (rowData) => {
        dispatch(setServiceOrderId(rowData.service_order_id));
        dispatch(setEngineeringReportId(rowData.id));

        navigate(
          `/biomedicalManagementDashboard/serviceRecords/createEngineeringReport`
        );
      }
    }
  ];

  const isModuleAdmin = isRoleUtil(BIOMEDICAL_MODULE_ADMIN, userData.roleList);
  const canEditEngineeringReport = hasAnyRoleUtil(
    BIOMEDICAL_ENGINEERING_REPORT_EDIT_ROLES,
    userData.roleList
  );
 
  const handleClearFilters = () => {
    reset(EMPTY_FILTERS);
    clearFilters();
    setAppliedFilters(EMPTY_FILTERS);
    setCurrentPage(1);
  };

  const handleFilter = () => {
    const newFilters = getValuesTable();
    setFilters(newFilters);
    setAppliedFilters(newFilters);
    setCurrentPage(1);
  };

  const serviceOrderOptions = listOptions.orders;
  const institutionOptions = listOptions.institutions;
  const dateCompletionOptions = generateSelectList(
    listOptions.dates,
    "value",
    "value",
    true
  );

  useEffect(() => {
    getFilterOptions();
    getSelectData();
  }, []);

  useEffect(() => {
    if (!optionsReady) return;
    getTableData();
    return () => { requestSequence.current += 1; };
  }, [currentPage, appliedFilters, listOptions, optionsReady]);

  return (
    <div>
      <BreadCrumbComp />
      <div className="searchTitle">
        <h1>REPORTE DE INGENIERÍA</h1>
      </div>

      <div>
        <br className="br-white-space" />
        <div className="mt-25 mb-10">
          <form
            style={{
              display: "flex",
              alignItems: "center"
            }}
          >
            <Grid container spacing={2} alignItems="flex-end" className="biomedical-filters">
              <Grid item xs={12} sm={2} md={2}>
                <MultiSelect
                  name="serviceOrderId"
                  control={controlTable}
                  label="Nro de OS"
                  options={serviceOrderOptions}
                  margin={"0"}
                  isMultiple={false}
                  searchBar={true}
                />
              </Grid>
              <Grid item xs={12} sm={2} md={2}>
                <MultiSelect
                  name="serieNumber"
                  control={controlTable}
                  label="Nro. de serie"
                  options={serieOId}
                  margin={"0"}
                  isMultiple={false}
                  searchBar={true}
                />
              </Grid>
              <Grid item xs={12} sm={2} md={2} sx={{
                minWidth: 0,
                "& .MuiAutocomplete-inputRoot": {
                  height: "40px",
                  flexWrap: "nowrap",
                  alignItems: "center",
                  paddingTop: "0 !important",
                  paddingBottom: "0 !important",
                  paddingRight: "32px !important"
                },
                "& .MuiAutocomplete-inputRoot .MuiAutocomplete-input": {
                  minWidth: "0 !important",
                  width: "0 !important",
                  flexGrow: 1
                },
                "& .MuiChip-root": {
                  minWidth: 0,
                  maxWidth: "calc(100% - 8px) !important",
                  height: "30px",
                  marginTop: 0,
                  marginBottom: 0,
                  flexShrink: 1,
                  display: "flex",
                  alignItems: "center"
                },
                "& .MuiChip-label": {
                  display: "block",
                  height: "100%",
                  lineHeight: "30px",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap"
                }
              }}>
                <MultiSelect
                  name="institution"
                  control={controlTable}
                  label="Institución"
                  options={institutionOptions}
                  margin={"0"}
                  isMultiple={true}
                  searchBar={true}
                />
              </Grid>
              <Grid item xs={12} sm={2} md={2}>
                <SimpleSelect
                  name="DateCompletion"
                  control={controlTable}
                  label="Fecha de realización"
                  options={dateCompletionOptions}
                  margin={"0"}
                />
              </Grid>
              <Grid item xs={12} sm={2} md={2}>
                <SimpleSelect
                  name="engineer"
                  control={controlTable}
                  label="Ingeniero/a"
                  options={engineerList}
                  margin={"0"}
                />
              </Grid>
              <Grid item xs={12} sm={2} md={2}>
                <div className="IHContainer j-end">
                  <LaddaButton
                    variant="contained"
                    onClick={handleFilter}
                    margin={"0"}
                  >
                    <img
                      className="btn-icon"
                      src={icons.filterWhite}
                      alt="icon"
                    />
                  </LaddaButton>
                  <LaddaButton
                    variant="contained"
                    onClick={handleClearFilters}
                    margin={"0 0 0 10px"}
                  >
                    <img
                      className="btn-icon"
                      src={icons.clearFilterWhite}
                      alt="icon"
                    />
                  </LaddaButton>
                </div>
              </Grid>
            </Grid>
          </form>
        </div>
        {tableError && <p role="alert">{tableError}</p>}
        <div className="biomedical-summary-table biomedical-engineering-report-table biomedical-rounded-table">        
        <SimpleTable
          loading={loadingTable}
          data={tableData}
          serverPagination
          totalRecords={totalRecords}
          currentPage={currentPage}
          rowsPerPage={pageSize}
          hidePaginatorRowsDropdown
          onPageChange={setCurrentPage}
          tableStyle={{ tableLayout: "fixed", width: "100%", minWidth: "1200px" }}
          columns={engineeringReportColumns}
          text={"Realizar Autoevaluación"}
          isPaginated={true}
          sortable
          buttons={buttons}
          showItemCount={true}
        />
        </div>
      </div>
    </div>
  );
};

export default ServiceRecords;
